import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import Infinite from './Infinite'
import DayDivider from './DayDivider'

import { createTimeline, getShowableOld, getCurrentTimeline } from './helpers'
import useAccess, { PERMS } from '../../../../hooks/useAccess'
import { useSocket } from '../../../../providers/SocketProvider'
import { useUserAPI } from '../../../../providers/UserAPIProvider'
import { fetchTags } from '../../../../slices/tag'
import { useTaskAPI } from '../../../../providers/TaskAPIProvider'
import { selectConvo } from '../../../../slices/convo'
import { selectDMs } from '../../../../slices/dms'
import { useReadStatus } from '../../../../providers/ReadStatusProvider'
import { useAddRemoveParticipant } from '../../../../providers/AddRemoveParticipantProvider'
import {
  useTimelinePagination,
  PAGE_SIZE
} from '../../../../providers/TimelinePaginationProvider'
import { useOwner } from '../../../../providers/OwnerProvider'
import { useVerification } from '../../../../providers/VerificationProvider'
import { useSurvey } from '../../../../providers/SurveyProvider'
import useStyles from './styles'

const Timeline = ({
  search,
  session,
  targetID,
  targetRef,
  isDirect,
  empty,
  isClosed,
  containerRef,
  filterNotes,
  viewingUser,
  viewing
}) => {
  const dispatch = useAppDispatch()

  const {
    api: { handleFetchResult }
  } = useSocket()
  const { allConversations } = useAppSelector(selectConvo)
  const { allUsers } = useUserAPI()
  const tagsSelector = useAppSelector(storeState => storeState.tag)
  const { tags } = tagsSelector
  const { tasks: allTasks } = useTaskAPI()
  const { dms } = useAppSelector(selectDMs)
  const { convos } = useReadStatus()
  const { setEditSessionUsers } = useAddRemoveParticipant()
  const hasPermission = useAccess()
  const { getData, isDoneLoading } = useTimelinePagination()
  const { deleted: ownerDeleted } = useOwner()
  const { verified } = useVerification() ?? { verified: true }
  const { getSurveys } = useSurvey()

  const conversations = React.useMemo(
    () => (isDirect ? dms : allConversations),
    [isDirect, dms, allConversations]
  )

  const doneLoading = React.useMemo(() => {
    const id = conversations[session]?.owner_id ?? session
    return isDoneLoading(id)
  }, [conversations, session, isDoneLoading])

  const convo = React.useMemo(
    () =>
      conversations[session] ?? {
        messages: [],
        notes: [],
        startDate: Date.now(),
        endDate: null
      },
    [conversations, session]
  )

  const surveys = React.useMemo(() => {
    if (isDirect) {
      return []
    }

    return getSurveys(session)
  }, [isDirect, getSurveys, session])

  const lastRead = React.useMemo(() => {
    if (!convo.session || !(convo.session in convos)) {
      return new Date(null)
    }

    if (convo.users?.length !== Object.keys(convos[convo.session])?.length) {
      return new Date(null)
    }

    const vals = Object.values(convos[convo.session])
    if (!vals.length) {
      return new Date(null)
    }

    const date = vals.reduce((a, b) => (a < b ? a : b))

    return date
  }, [convo, convos])

  const canViewNotes = React.useMemo(
    () => hasPermission(PERMS.NotesFull),
    [hasPermission]
  )

  const p2p = React.useMemo(() => {
    // removing blue ticks from p2p chats
    return !convo.isGroup && !convo.owner_id
  }, [convo])

  const allTags = React.useMemo(() => [...new Set(tags)], [tags])

  // Get owner
  const owner = React.useMemo(() => {
    const ownerID = convo.owner_id
    if (ownerID) {
      return allUsers[ownerID]
    }

    return null
  }, [convo.owner_id, allUsers])

  // Find tasks for this user
  const extraTasks = React.useMemo(() => {
    if (!owner) {
      return []
    }

    const taskIDs = (owner.tasks ?? []).map(task => task.id)
    const tasks = []

    const foundIDs = []
    Object.values(allTasks).forEach(task => {
      const taskFound =
        taskIDs.includes(task.id) ||
        (task.concerning_user_id &&
          [owner.id, convo.owner_id].includes(task.concerning_user_id))

      // Valid if loaded and not in a session
      const isValid =
        Object.keys(task).length > 2 && (task.session_ids?.length ?? 0) === 0
      if (taskFound && isValid && !foundIDs.includes(task.id)) {
        tasks.push(['TASK', new Date(task.created_at), task])
        foundIDs.push(task.id)
      }
    })

    return tasks
  }, [owner, allTasks, convo])

  const [collapsed, setCollapsed] = React.useState([])

  const classes = useStyles()

  const { messages, notes, startDate, endDate } = convo

  const [oldTimelines, oldStarts, currentTimeline, hasMultipleSessions] =
    React.useMemo(
      () =>
        createTimeline({
          messages,
          notes,
          tasks: Object.values(allTasks).filter(t => !t.deleted_at),
          surveys,
          convo,
          viewing,
          viewingUser,
          canViewNotes,
          endDate,
          session,
          filterNotes,
          search,
          allTags,
          allUsers,
          classes,
          targetID,
          targetRef,
          lastRead,
          p2p,
          isDirect
        }),
      [
        messages,
        notes,
        allTasks,
        surveys,
        convo,
        viewing,
        viewingUser,
        canViewNotes,
        endDate,
        session,
        filterNotes,
        search,
        allTags,
        allUsers,
        classes,
        targetID,
        targetRef,
        lastRead,
        p2p,
        isDirect
      ]
    )

  const toggleCollapse = React.useCallback(
    id => () => {
      setCollapsed(old => {
        if (old.includes(id)) {
          return old.filter(x => x !== id)
        }

        return [...new Set([...old, id])]
      })
    },
    []
  )

  const combinedOld = React.useMemo(
    () => [...oldStarts, ...extraTasks].sort((a, b) => a[1] - b[1]),
    [oldStarts, extraTasks]
  )

  const showableOld = React.useMemo(
    () =>
      getShowableOld({
        allTags,
        classes,
        collapsed,
        combinedOld,
        oldTimelines,
        toggleCollapse
      }),
    [allTags, classes, collapsed, combinedOld, oldTimelines, toggleCollapse]
  )

  const current = React.useMemo(
    () =>
      getCurrentTimeline({
        empty,
        doneLoading,
        hasMultipleSessions,
        isDirect,
        verified,
        allUsers,
        classes,
        convo,
        currentTimeline,
        ownerDeleted,
        session,
        setEditSessionUsers,
        startDate
      }),
    [
      empty,
      doneLoading,
      hasMultipleSessions,
      isDirect,
      verified,
      allUsers,
      classes,
      convo,
      currentTimeline,
      ownerDeleted,
      session,
      setEditSessionUsers,
      startDate
    ]
  )

  const fullTimeline = React.useMemo(() => {
    const full = []

    if (empty && isDirect && currentTimeline.length > 0) {
      full.push(
        <DayDivider
          key='day-div-start'
          date={currentTimeline[0].props?.task?.date ?? new Date(null)}
        />
      )
    }

    full.push(...showableOld)

    full.push(...current)

    return full
  }, [empty, isDirect, currentTimeline, showableOld, current])

  React.useEffect(() => {
    if (!tagsSelector.loaded && !tagsSelector.loading) {
      dispatch(fetchTags())
    }
  }, [tagsSelector, dispatch])

  return (
    <>
      {isClosed && <div style={{ minHeight: '2rem' }} />}

      {empty && isDirect && currentTimeline.length === 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            fontStyle: 'italic'
          }}
        >
          No recent activity
        </div>
      )}

      {/* @ts-ignore */}
      <Infinite
        key={session}
        id={conversations[session]?.owner_id ?? session}
        containerRef={containerRef}
        getData={getData}
        onDataFetched={handleFetchResult}
        PAGE_SIZE={PAGE_SIZE}
      >
        {fullTimeline}
      </Infinite>
    </>
  )
}

export default Timeline
